import React, { useState, useEffect } from 'react';
import SimpleQRScanner from './SimpleQRScanner';
import { Drink, Person, Sale } from '../App';
import './BarInterface.css';

interface BarInterfaceProps {
  drinks: Drink[];
  persons: Person[];
  salesHistory: Sale[];
  onPurchase: (drinkId: number, quantity: number, personId?: number) => Promise<any>;
}

const BarInterface: React.FC<BarInterfaceProps> = ({ drinks, persons, salesHistory, onPurchase }) => {
  const [selectedDrink, setSelectedDrink] = useState<number | null>(null);
  const [selectedPerson, setSelectedPerson] = useState<number | null>(null);
  const [quantity, setQuantity] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [scannerError, setScannerError] = useState<string>('');

  // Removed automatic refresh - data updates via Socket.io in App.tsx

  const handlePurchase = async () => {
    if (!selectedDrink) {
      setMessage('❌ Bitte wähle ein Getränk aus!');
      return;
    }

    if (persons.length > 0 && !selectedPerson) {
      setMessage('❌ Bitte wähle eine Person für die Rechnung aus!');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const result = await onPurchase(selectedDrink, quantity, selectedPerson || undefined);
      const drink = drinks.find(d => d.id === selectedDrink);
      const person = selectedPerson ? persons.find(p => p.id === selectedPerson) : null;
      
      let baseMessage = `✅ ${quantity}x ${drink?.name} verkauft`;
      if (person) {
        baseMessage += ` an ${person.name}`;
      }
      
      if (result.priceChange > 0) {
        setMessage(`${baseMessage}! Preis steigt um €${result.priceChange.toFixed(2)}`);
      } else if (result.priceChange < 0) {
        setMessage(`${baseMessage}! Preis fällt um €${Math.abs(result.priceChange).toFixed(2)}`);
      } else {
        setMessage(`${baseMessage}!`);
      }
      
      setSelectedDrink(null);
      setSelectedPerson(null);
      setQuantity(1);
    } catch (error) {
      setMessage('❌ Fehler beim Verkauf!');
    } finally {
      setLoading(false);
    }
  };

  const quickQuantities = [1, 2, 3, 5, 10];

  const handleQRScan = async (qrData: string) => {
    setScannerError('');
    try {
      // Extract person ID from QR code data (format: GUEST-{id})
      const match = qrData.match(/^GUEST-(\d+)$/);
      if (!match) {
        setScannerError('Ungültiger QR-Code Format');
        return;
      }

      const personId = parseInt(match[1]);
      const person = persons.find(p => p.id === personId);
      
      if (person) {
        setSelectedPerson(personId);
        setShowQRScanner(false);
        setMessage(`✅ ${person.name} ausgewählt`);
      } else {
        setScannerError('Person nicht gefunden');
      }
    } catch (error) {
      setScannerError('Fehler beim Scannen des QR-Codes');
    }
  };

  const startQRScanner = () => {
    setShowQRScanner(true);
    setScannerError('');
  };

  const stopQRScanner = () => {
    setShowQRScanner(false);
    setScannerError('');
  };

  return (
    <div className="bar-interface">
      <h2>🍺 Bar-Terminal</h2>
      
      {message && (
        <div className={`message ${message.includes('✅') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      <div className="drinks-grid">
        {drinks.map(drink => (
          <div
            key={drink.id}
            className={`drink-card ${selectedDrink === drink.id ? 'selected' : ''}`}
            onClick={() => setSelectedDrink(drink.id)}
          >
            <div className="drink-icon">{drink.icon}</div>
            <div className="drink-name">{drink.name}</div>
            <div className="drink-price">€{drink.currentPrice.toFixed(2)}</div>
            {drink.currentPrice > drink.basePrice && (
              <div className="price-indicator up">↑ +{((drink.currentPrice - drink.basePrice) / drink.basePrice * 100).toFixed(0)}%</div>
            )}
            {drink.currentPrice < drink.basePrice && (
              <div className="price-indicator down">↓ {((drink.currentPrice - drink.basePrice) / drink.basePrice * 100).toFixed(0)}%</div>
            )}
          </div>
        ))}
      </div>

      {persons.length > 0 && (
        <div className="person-section">
          <div className="person-section-header">
            <h3>Person für Rechnung:</h3>
            <button onClick={startQRScanner} className="qr-scanner-btn">
              📱 QR scannen
            </button>
          </div>
          
          {scannerError && (
            <div className="scanner-error-message">
              {scannerError}
            </div>
          )}
          <div className="persons-grid">
            <div
              className={`person-card ${selectedPerson === null ? 'selected' : ''}`}
              onClick={() => setSelectedPerson(null)}
            >
              <div className="person-icon">💰</div>
              <div className="person-name">Bar-Kauf</div>
              <div className="person-detail">Ohne Personenzuordnung</div>
            </div>
            {persons.map(person => (
              <div
                key={person.id}
                className={`person-card ${selectedPerson === person.id ? 'selected' : ''}`}
                onClick={() => setSelectedPerson(person.id)}
              >
                <div className="person-icon">👤</div>
                <div className="person-name">{person.name}</div>
                <div className="person-detail">
                  {person.purchaseCount} Käufe | €{person.totalSpent.toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="quantity-section">
        <h3>Menge:</h3>
        <div className="quantity-buttons">
          {quickQuantities.map(q => (
            <button
              key={q}
              className={`quantity-btn ${quantity === q ? 'selected' : ''}`}
              onClick={() => setQuantity(q)}
            >
              {q}
            </button>
          ))}
        </div>
        <div className="custom-quantity">
          <input
            type="number"
            min="1"
            max="99"
            value={quantity}
            onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
          />
        </div>
      </div>

      <button
        className="purchase-button"
        onClick={handlePurchase}
        disabled={!selectedDrink || loading}
      >
        {loading ? 'Verarbeite...' : `Verkaufen (€${
          selectedDrink ? (drinks.find(d => d.id === selectedDrink)?.currentPrice || 0) * quantity : 0
        })`}
      </button>

      <div className="sales-history">
        <h3>📋 Verkaufshistorie</h3>
        <div className="sales-list">
          {salesHistory.slice(-20).reverse().map(sale => (
            <div key={sale.id} className="sale-item">
              <div className="sale-info">
                <span className="sale-drink">{sale.drinkName}</span>
                <span className="sale-quantity">×{sale.quantity}</span>
                <span className="sale-price">€{sale.totalCost.toFixed(2)}</span>
              </div>
              <div className="sale-details">
                {sale.personName ? (
                  <span className="sale-person">👤 {sale.personName}</span>
                ) : (
                  <span className="sale-person">💰 Bar-Kauf</span>
                )}
                <span className="sale-time">
                  {new Date(sale.timestamp).toLocaleTimeString('de-DE')}
                </span>
              </div>
            </div>
          ))}
          {salesHistory.length === 0 && (
            <div className="no-sales">Noch keine Verkäufe</div>
          )}
        </div>
      </div>
      
      {showQRScanner && (
        <SimpleQRScanner
          onScan={handleQRScan}
          onError={(error) => setScannerError(error)}
          onClose={stopQRScanner}
        />
      )}
    </div>
  );
};

export default BarInterface;